class ParallaxController {
  private isEnabled: boolean = true;
  private parallaxElements: NodeListOf<Element> | null = null;
  private ticking: boolean = false;

  constructor() {
    this.init();
  }

  private init(): void {
    this.checkMobileDevice();
    this.bindEvents();
    this.updateElements();
  }

  private checkMobileDevice(): void {
    // Enable parallax on all devices now
    this.isEnabled = true;
  }

  private bindEvents(): void {
    // Throttled scroll event listener
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    // Re-check mobile state on resize
    window.addEventListener('resize', this.throttle(() => {
      this.checkMobileDevice();
      this.updateElements();
    }, 250));
  }

  private handleScroll(): void {
    if (!this.isEnabled || !this.parallaxElements) return;

    if (!this.ticking) {
      requestAnimationFrame(this.updateParallax.bind(this));
      this.ticking = true;
    }
  }

  private updateParallax(): void {
    if (!this.isEnabled || !this.parallaxElements) {
      this.ticking = false;
      return;
    }

    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    this.parallaxElements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      const speed = parseFloat(htmlElement.getAttribute('data-parallax-speed') || '0.5');
      
      // Calculate the element's position relative to viewport
      const elementTop = htmlElement.offsetTop;
      const elementHeight = htmlElement.offsetHeight;
      const windowHeight = window.innerHeight;
      
      // Only apply parallax if element is in or near viewport
      if (scrollY + windowHeight > elementTop && scrollY < elementTop + elementHeight) {
        const yPos = -(scrollY - elementTop) * speed;
        htmlElement.style.transform = `translateY(${yPos}px)`;
      }
    });

    this.ticking = false;
  }

  private updateElements(): void {
    // Re-query parallax elements
    this.parallaxElements = document.querySelectorAll('[data-parallax-speed]');
    
    // Reset transforms on mobile
    if (!this.isEnabled && this.parallaxElements) {
      this.parallaxElements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.transform = '';
      });
    }
  }

  private throttle(func: Function, limit: number): (...args: any[]) => void {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Public method to manually refresh elements
  public refresh(): void {
    this.updateElements();
  }

  // Public method to enable/disable parallax
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!this.isEnabled) {
      this.updateElements(); // This will reset transforms
    }
  }

  // Public method to destroy the parallax controller
  public destroy(): void {
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('resize', this.checkMobileDevice.bind(this));
    
    if (this.parallaxElements) {
      this.parallaxElements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.transform = '';
      });
    }
  }
}

// Export singleton instance
export const parallaxController = new ParallaxController();

// Export utility function for easy integration
export const initParallax = (): void => {
  parallaxController.refresh();
};

// Export function to add parallax to an element
export const addParallaxElement = (
  element: HTMLElement, 
  speed: number = 0.5
): void => {
  element.setAttribute('data-parallax-speed', speed.toString());
  parallaxController.refresh();
};

export default parallaxController;